import axios from "axios";
import qs from "qs";
import { notification } from "antd";
import { ActionType } from "../constants/ActionTypes";
import { ErrorType } from "../constants/Errors/ErrorTypes";
import { RoutePath, RestPath } from "../constants/SitePaths";
import { notVerifiedAccount } from "./account/VerificationActions";
import { Constants } from "../constants/Constants";
import history from "../services/History";
import { Auth } from "../services/Auth";
import config from "../constants/Configs";
import { getAppUrl } from "../services/AppBasePath";

/**
 * The in-flight refresh CYCLE: { sessionGeneration, subscribers: [...] }. Exactly ONE
 * refresh runs per logical session generation — concurrent 401s of the SAME session
 * dedupe into it — while a NEWER session (logout -> new login / authorize / SSO storage
 * replacement) starts its OWN cycle that an older cycle's completion can never touch
 * (each cycle settles ONLY the subscribers queued on it, never the global state).
 * `null` once the cycle has settled.
 */
let inFlightRefresh = null;

/**
 * The explicit stale-auth rejection: async auth work completed under a session that has
 * been REPLACED. AxiosMiddleware remaps it onto the ORIGINAL request failure (which
 * keeps its own response props) — never a fabricated success, never a silently dropped
 * (unsettled) promise.
 */
const staleAuthContextError = () => {
	const error = new Error("stale auth context: the session was replaced while the request was in flight");
	error.isStaleAuthContext = true;
	return error;
};

/** Settle (drain) one cycle's own subscriber queue — never any other queue. */
function settleRefreshSubscribers(cycle, settle) {
	const queue = cycle.subscribers;
	cycle.subscribers = [];
	queue.forEach(settle);
}

// User Logout
export const logout = () => ({
	type: ActionType.USER_LOGOUT,
});

// User Login

export const resetLoading = () => ({
	type: ActionType.RESET_LOADING,
});

export const loginUserBegin = () => ({
	type: ActionType.LOGIN_USER_BEGIN,
});

export const loginUserSuccess = user => ({
	type: ActionType.LOGIN_USER_SUCCESS,
	payload: { user },
});

export const loginUserError = (user, error) => ({
	type: ActionType.LOGIN_USER_FAILURE,
	payload: { error, user },
});

// Get User Data
// Get User Data
export const getUserDataBegin = () => ({
	type: ActionType.GET_USER_DATA_BEGIN,
});
export const getUserDataSuccess = data => ({
	type: ActionType.GET_USER_DATA_SUCCESS,
	payload: data,
});
export const getUserDataError = error => ({
	type: ActionType.GET_USER_DATA_ERROR,
	payload: error,
});
export const setPasswordBegin = () => ({
	type: ActionType.SET_PASSWORD_BEGIN,
});

export const setPasswordSuccess = user => ({
	type: ActionType.SET_PASSWORD_SUCCESS,
	payload: { user },
});

export const setPasswordError = error => ({
	type: ActionType.SET_PASSWORD_ERROR,
	payload: error,
});

export const checkHashBegin = () => ({
	type: ActionType.CHECK_HASH_BEGIN,
});

export const checkHashError = user => ({
	type: ActionType.CHECK_HASH_SUCCESS,
	payload: { user },
});

export const checkHashSuccess = error => ({
	type: ActionType.CHECK_HASH_ERROR,
	payload: error,
});

export const getLocationSuccess = data => ({
	type: ActionType.GET_LOCATION_SUCCESS,
	payload: data,
});

export const updateAbilities = abilities => ({
	type: ActionType.UPDATE_ABILITIES,
	payload: { abilities },
});

export const getCompaniesBegin = () => ({
	type: ActionType.GET_COMPANIES_BEGIN,
});
export const getCompaniesSuccess = data => ({
	type: ActionType.GET_COMPANIES_SUCCESS,
	payload: data,
});
export const getCompaniesError = error => ({
	type: ActionType.GET_COMPANIES_ERROR,
	payload: error,
});

export const getAllCompaniesBegin = () => ({
	type: ActionType.GET_ALL_COMPANIES_BEGIN,
});
export const getAllCompaniesSuccess = data => ({
	type: ActionType.GET_ALL_COMPANIES_SUCCESS,
	payload: data,
});
export const getAllCompaniesError = error => ({
	type: ActionType.GET_ALL_COMPANIES_ERROR,
	payload: error,
});

// Two factor auth

export const generateTwoFactorAuthBegin = () => ({
	type: ActionType.GENERATE_TFA_CODE_BEGIN,
});

export const generateTwoFactorAuthSuccess = data => ({
	type: ActionType.GENERATE_TFA_CODE_SUCCESS,
	payload: data,
});

export const generateTwoFactorAuthError = error => ({
	type: ActionType.GENERATE_TFA_CODE_FAILURE,
	payload: error,
});

export const recoverTwoFactorAuthBegin = () => ({
	type: ActionType.RECOVER_TFA_BEGIN,
});

export const recoverTwoFactorAuthSuccess = data => ({
	type: ActionType.RECOVER_TFA_SUCCESS,
	payload: data,
});

export const recoverTwoFactorAuthError = error => ({
	type: ActionType.RECOVER_TFA_FAILURE,
	payload: error,
});

/**
 * The login captcha provider form-field value. ORIGIN-VERIFIED (read-only sibling repo
 * Builder, origin/prd @ ac4ee658 — AccountService.cs lines 811-826 + Common/Constants.cs
 * lines 177-188): once the failed-login threshold is exceeded, the /token password grant
 * REQUIRES the `captchaProvider` form field — Enum.TryParse<CaptchaTypes> (case-sensitive)
 * must yield CaptchaTypes.GoogleV2, i.e. the enum NAME "GoogleV2" (or its byte value "3").
 * The builder's widget is Google reCAPTCHA v2 (react-google-recaptcha,
 * src/routes/account/RecaptchaFormItem.js), so "GoogleV2" is the exact provider identity.
 */
const LOGIN_CAPTCHA_PROVIDER = "GoogleV2";

/** User Login */

export function loginUser(data) {
	Auth.invalidateSession();
	return dispatch => {
		dispatch(loginUserBegin());

		const params = {
			username: data.username,
			password: data.password,
			grant_type: "password",
			client_id: config.APP_CLIENT_ID,
		};

		data.captchaCode && (params.captcha = data.captchaCode);
		data.TFACode && (params.TFACode = data.TFACode);
		// The provider field is sent ONLY together with a solved captcha token: the first
		// ordinary request must not carry it, and a solved token carries BOTH fields
		// (the ORIGIN gate checks the provider BEFORE the captcha token — see the
		// LOGIN_CAPTCHA_PROVIDER source note above).
		if (data.captchaCode) {
			params.captchaProvider = LOGIN_CAPTCHA_PROVIDER;
		}
		// Encode the data
		const body = Object.keys(params)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
			.join("&");
		return axios({
			url: `${config.BASE_URL}/token`,
			method: "POST",
			headers: {
				"Content-Type": "x-www-form-urlencoded",
			},
			data: body,
		})
			.then(response => {
				// A NEW logical session (raw storage replacement kept verbatim through
				// Auth.setAuthData) — the session generation advances so async work bound
				// to the previous session can detect it is stale.
				Auth.setAuthData(response.data);
				dispatch(loginUserSuccess(response.data));

				return { status: 200, data: response.data };
			})
			.catch(error => {
				if (
					error.response &&
					error.response.status === 400 &&
					error.response.data.code &&
					(error.response.data.code === ErrorType.MOBILE_NOT_VERIFIED ||
						error.response.data.code === ErrorType.EMAIL_AND_MOBILE_NOT_VERIFIED ||
						error.response.data.code === ErrorType.EMAIL_NOT_VERIFIED)
				) {
					dispatch(loginUserError(params, error.response.data));
					dispatch(notVerifiedAccount(error.response && error.response.data.code));
					history.push(getAppUrl(RoutePath.ACCOUNT_VERIFICATION));
				} else if (error.response) {
					dispatch(loginUserError(params, error.response.data));
				}

				return { status: 400, data: error.response && error.response.data };
			});
	};
}

export function authorize(token) {
	// Thunk intentionally does not dispatch — stores auth data in LocalStorage only
	return () => {
		const params = `token=${token}`;
		return axios({
			url: `${config.BASE_URL + RestPath.AUTHORIZE_GET}?${params}`,
			method: Constants.GET,
		})
			.then(response => {
				// A NEW logical session (the SSO callback payload kept verbatim through
				// Auth.setAuthData) — the session generation advances.
				Auth.setAuthData(response.data);
				return true;
			})
			.catch(() => false);
	};
}

export function refreshToken(authData, request) {
	// SNAPSHOT the credential identity BEFORE any async work: `authData` may be the
	// MUTABLE Auth singleton (src/services/AxiosMiddleware.js passes its fillAndGetData()
	// return straight through), so this flow binds to the values captured HERE — never
	// to later mutations of that object.
	const capturedRefreshToken = authData && authData.refreshToken;
	const capturedSessionGeneration = authData && authData.sessionGeneration;

	const params = {
		refresh_token: capturedRefreshToken,
		grant_type: "refresh_token",
		client_secret: "748434cf2eeca18cd7be9661dce8c39d60f16461c8e760426958493b9f79b52f",
		client_id: config.APP_CLIENT_ID,
	};

	/**
	 * Revalidate the captured session against the CURRENT one on every completion.
	 * Re-reads storage through Auth.fillAndGetData() (it adopts external replacements
	 * and advances the generation for real transitions). A captured generation of
	 * undefined (legacy/unstamped callers) never goes stale — the same contract
	 * AxiosMiddleware keeps for unstamped requests.
	 */
	const sessionStillCurrent = () => {
		const current = Auth.fillAndGetData();
		if (capturedSessionGeneration === undefined || !current || current.sessionGeneration === undefined) {
			return true;
		}
		return current.sessionGeneration === capturedSessionGeneration;
	};

	const cycle = { sessionGeneration: capturedSessionGeneration, subscribers: [] };
	const joinsInFlightRefresh = inFlightRefresh && inFlightRefresh.sessionGeneration === capturedSessionGeneration;
	const activeCycle = joinsInFlightRefresh ? inFlightRefresh : cycle;
	if (!joinsInFlightRefresh) {
		inFlightRefresh = cycle;
		axios({
			method: "POST",
			url: `${config.BASE_URL}/token`,
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			data: qs.stringify(params),
			// Frontend-only request metadata (never headers/body): marks THIS POST as the
			// refresh itself, so the response interceptor never refreshes-on-the-refresh and
			// the invalid_grant logout below runs EXACTLY once (here, not also there).
			authRefreshRequest: true,
		})
			.then(response => {
				if (inFlightRefresh === cycle) {
					inFlightRefresh = null;
				}
				// REVALIDATE on SUCCESS before ANY persistence, replay or SSO publication:
				// a refresh RELEASED after logout / a new login must never restore the old
				// authorizationData (BR-LOGOUT-03) and must never clear a newer queue — this
				// cycle settles ONLY its own subscribers.
				if (!sessionStillCurrent()) {
					settleRefreshSubscribers(cycle, subscriber => subscriber.reject(staleAuthContextError()));
					return response.data;
				}
				if (response.status < 400) {
					// Token rotation inside the SAME logical session (no generation advance).
					Auth.adoptRefreshedAuthData(response.data);
				}
				const accessToken = response.data && response.data.access_token;
				settleRefreshSubscribers(cycle, subscriber => {
					subscriber.request.headers.Authorization = `Bearer ${accessToken}`;
					subscriber.resolve(axios(subscriber.request));
				});
				if (config.IS_SSO_ENABLED) {
					const accounFrameWindow = document.getElementById("account-frame").contentWindow;
					accounFrameWindow.postMessage(
						JSON.stringify({ key: "authorizationData", method: "update", data: response.data }),
						"*",
					);
				}

				return response.data;
			})
			.catch(error => {
				if (inFlightRefresh === cycle) {
					inFlightRefresh = null;
				}
				// REVALIDATE on FAILURE too: a stale failure must not invalidate or reload
				// the NEW session and must not clear a newer queue. A CURRENT genuine
				// failure settles every queued subscriber with the REAL error (the same
				// error contract for all of them) and logs the user out EXACTLY once.
				if (!sessionStillCurrent()) {
					settleRefreshSubscribers(cycle, subscriber => subscriber.reject(staleAuthContextError()));
					return undefined;
				}
				settleRefreshSubscribers(cycle, subscriber => subscriber.reject(error));
				Auth.invalidateSession();
				window.location.reload();
			});
	}

	const retryOriginalRequest = new Promise((resolve, reject) => {
		activeCycle.subscribers.push({ request, resolve, reject });
	});
	return retryOriginalRequest;
}

export function getLocation() {
	return dispatch =>
		// dispatch(getLocationBegin());
		axios({
			url: config.BASE_URL + RestPath.GET_LOCATION,
			method: Constants.GET,
		})
			.then(response => {
				dispatch(getLocationSuccess(response.data));
				return response.data;
			})
			.catch(() => {
				// dispatch(getLocationError(error.response && error.response.data));
			});
}

/*  Get User Data  */
export function getUserData(params) {
	return dispatch => {
		dispatch(getUserDataBegin());

		return axios({
			url: `${config.BASE_URL}${RestPath.GET_USER_DATA}`,
			method: Constants.GET,
			data: JSON.stringify(params),
		})
			.then(response => {
				dispatch(getUserDataSuccess(response.data));
				return true;
			})
			.catch(error => {
				dispatch(getUserDataError(error.response && error.response.data));
			});
	};
}

export function getCompanies(params, storeAsAllCompaines) {
	return dispatch => {
		dispatch(getCompaniesBegin());
		// const params = {
		//    'filter.companyId': 1
		//   'filter.projectState': 2
		// }

		return axios({
			url: config.BASE_URL + RestPath.GET_USER_COMPANIES,
			method: Constants.GET,
			headers: {
				"Content-Type": "application/json",
			},
			params,
		})
			.then(response => {
				// Success
				dispatch(getCompaniesSuccess(response.data));
				storeAsAllCompaines && dispatch(getAllCompaniesSuccess(response.data));
			})
			.catch(error => {
				dispatch(getCompaniesError(error.response && error.response.data));
			});
	};
}

export function getAllCompanies(params) {
	return dispatch => {
		dispatch(getAllCompaniesBegin());

		return axios({
			url: config.BASE_URL + RestPath.GET_ALL_USER_COMPANIES,
			method: Constants.GET,
			headers: {
				"Content-Type": "application/json",
			},
			params,
		})
			.then(response => {
				// Success
				dispatch(getAllCompaniesSuccess(response.data));
			})
			.catch(error => {
				dispatch(getAllCompaniesError(error.response && error.response.data));
			});
	};
}

export function setPassword(data, isReset) {
	return dispatch => {
		dispatch(setPasswordBegin());

		return axios({
			url: config.BASE_URL + (isReset ? RestPath.CHANGE_EXPIRED_PASSWORD_BY_URL : RestPath.CHANGE_PASSWORD_BY_URL),
			method: "POST",
			data,
		})
			.then(response => {
				// Success
				dispatch(setPasswordSuccess(response.data));
				return true;
			})
			.catch(error => {
				dispatch(setPasswordError(error.response && error.response.data));
				return false;
			});
	};
}

export function checkHash(params) {
	return dispatch => {
		dispatch(checkHashBegin());

		return axios({
			url: config.BASE_URL + RestPath.CHECK_HASH,
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			params,
		})
			.then(response => {
				// Success
				dispatch(checkHashSuccess(response.data));
				return true;
			})
			.catch(error => {
				dispatch(checkHashError(error.response && error.response.data));
				return false;
			});
	};
}

export function generateTwoFactorAuth(email) {
	return dispatch => {
		dispatch(generateTwoFactorAuthBegin());
		return axios({
			url: config.BASE_URL + RestPath.GENERATE_TFA_CODE,
			method: "POST",
			data: { email },
		})
			.then(response => {
				dispatch(generateTwoFactorAuthSuccess(response.data));
				return true;
			})
			.catch(error => {
				dispatch(generateTwoFactorAuthError(error.response && error.response.data));
				return false;
			});
	};
}

export function recoverTwoFactorAuth(email) {
	return dispatch => {
		dispatch(recoverTwoFactorAuthBegin());
		return axios({
			url: config.BASE_URL + RestPath.RECOVER_TFA,
			method: "POST",
			data: { email },
		})
			.then(response => {
				dispatch(recoverTwoFactorAuthSuccess(response.data));
				notification.success({
					placement: "bottomRight",
					duration: 5,
					// AntD 6 notification API: the visible headline is `title` (the legacy
					// `message` config key is deprecated) — the SOURCED text stays unchanged.
					title: "QR code for 2 factor authentication has been sent to your email.",
					description: "",
				});
				return true;
			})
			.catch(error => {
				dispatch(recoverTwoFactorAuthError(error.response && error.response.data));
				return false;
			});
	};
}
